/**
 * TypeScript interfaces for Realtime Gateway
 * 
 * Defines all message types, events, and data structures used
 * for Server-Sent Events communication between Gateway and Copilot.
 */

import { z } from "zod";

// ============================================================================
// TRANSCRIPT
// ============================================================================

export interface TranscriptEntry {
  seq: number; // Unix epoch timestamp for ordering
  ts: number; // Timestamp in seconds relative to meeting start
  speaker: string; // "me" | "them" | "unknown"
  text: string; // Transcript text
}

export const transcriptEntrySchema = z.object({
  seq: z.number(),
  ts: z.number(),
  speaker: z.string(),
  text: z.string(),
});

// ============================================================================
// QUESTIONS
// ============================================================================

export interface QuestionSnapshot {
  id: number;
  question: string; // NOT "text"
  priority: number; // 0-1 score
  active: boolean;
  dismissed: boolean;
  reason?: string; // NOT "category"
  meeting_id?: number;
}

export const questionSnapshotSchema = z.object({
  id: z.number(),
  question: z.string(),
  priority: z.number(),
  active: z.boolean(),
  dismissed: z.boolean(),
  reason: z.string().optional(),
  meeting_id: z.number().optional(),
});

// ============================================================================
// GOALS
// ============================================================================

export interface GoalSignal {
  uid: string;
  type: string;
  summary: string;
  confidence: number;
  objective: string;
}

export interface GoalSnapshot {
  id: number;
  text: string; // NOT "title"
  progress: number; // 0-100 percentage
  priority: number; // 0-1 score (NOT derived)
  status: 'not_started' | 'in_progress' | 'completed'; // NOT derived from progress
  signal?: GoalSignal[]; // New field for Anaka Signals
  finding?: GoalSignal[]; // Backward compatibility for legacy "findings"
  seq_window?: string;
  gaps?: string[];
}

export const goalSnapshotSchema = z.object({
  id: z.number(),
  text: z.string(),
  progress: z.number(),
  priority: z.number(),
  status: z.enum(['not_started', 'in_progress', 'completed']),
  signal: z.array(z.object({
    uid: z.string(),
    type: z.string(),
    summary: z.string(),
    confidence: z.number(),
    objective: z.string(),
  })).optional(),
  finding: z.array(z.object({
    uid: z.string(),
    type: z.string(),
    summary: z.string(),
    confidence: z.number(),
    objective: z.string(),
  })).optional(),
  seq_window: z.string().optional(),
  gaps: z.array(z.string()).optional(),
});

// ============================================================================
// REDIS ENVELOPES
// ============================================================================

/**
 * Standard envelope format for Redis events
 */
export interface RedisEnvelope<T = any> {
  type: string; // Event type (e.g., "question.updated", "goal.updated")
  mid: number; // Meeting ID
  payload: T; // Event-specific payload
  ts: string; // ISO timestamp (e.g., "2025-10-09T20:18:45.550Z")
}

export const redisEnvelopeSchema = z.object({
  type: z.string(),
  mid: z.number(),
  payload: z.any(),
  ts: z.string(),
});

/**
 * Control stream events
 */
export type ControlEventType = "meeting.started" | "meeting.ended";

export interface ControlEvent {
  type: ControlEventType;
  mid: number;
  ts: string;
}

export const controlEventSchema = z.object({
  type: z.enum(["meeting.started", "meeting.ended"]),
  mid: z.number(),
  ts: z.string(),
});

/**
 * Analysis stream event types
 */
export type AnalysisEventType =
  | "goal.updated"
  | "question.created"
  | "question.updated"
  | "question.asked"
  | "question.archived"
  | "question.deactivated"
  | "triage.checkpoint"
  | "started.analyst"
  | "completed.analyst";


// ============================================================================
// PROJECT EVENTS (Project Agent)
// ============================================================================

/**
 * Project types matching Supabase ontology
 */
export type ProjectType = 'ProblemDiscovery' | 'ConceptFit' | 'SolutionFit' | 'Optimisation';

export type ProjectStatus = 'created' | 'scoping' | 'seeding' | 'planning' | 'ready' | 'active' | 'decision_needed' | 'completed' | 'archived';

/**
 * Statuses that count as "active" for UI lifecycle gating (ANA-266): a project
 * that has moved past seeding / planning. `decision_needed` is
 * active-but-awaiting-a-decision, so it qualifies too. Used to decide whether
 * the NavBar project switcher appears (see GlobalNavBar / ProjectTopBar).
 */
export const ACTIVE_PROJECT_STATUSES = ['active', 'decision_needed'] as const;

/** True when `status` is in the active lifecycle cohort ({@link ACTIVE_PROJECT_STATUSES}). */
export function isActiveProjectStatus(status: string | null | undefined): boolean {
  return status === 'active' || status === 'decision_needed';
}

/**
 * Early-lifecycle statuses: a project that exists but hasn't yet been scoped +
 * seeded into a working project. The v2 home hides its conversational chrome
 * (chat composer, suggestion pills, project switcher) for a SINGLE project in
 * one of these statuses; once the org has ≥2 projects the chrome always shows
 * (see `isEarlyLifecycleHome`). Drive this off the real `projects.status`
 * column (via `/api/v1/projects`), not the n8n lite projection.
 *
 * Canonical 9-stage values only; `isEarlyLifecycleProjectStatus` additionally
 * treats the legacy `draft` status as `created` (see below).
 */
export const EARLY_LIFECYCLE_PROJECT_STATUSES = ['created', 'scoping', 'seeding'] as const;

/**
 * True when `status` is an early-lifecycle status ({@link EARLY_LIFECYCLE_PROJECT_STATUSES}).
 * Legacy `draft` is treated as `created` so the gate is correct on both pre- and
 * post-9-stage-migration databases (prod's enum still emits `draft` for a
 * freshly-created project; migration 20260603000000 remaps `draft → created`).
 * Mirrors `useHomeLifecycleState`'s `STATUS_TO_STATE` (`draft: 4 // legacy → created`).
 */
export function isEarlyLifecycleProjectStatus(status: string | null | undefined): boolean {
  return (
    status === 'created' ||
    status === 'scoping' ||
    status === 'seeding' ||
    status === 'draft'
  );
}

/**
 * Statuses where the project's SCOPE IS LOCKED — scoping has finished and the
 * lifecycle has advanced past the open statuses (`created` / `scoping`, plus the
 * legacy pre-9-stage `draft`). `seeding` is the post-lock state graph promotion
 * writes, so it counts as locked.
 *
 * ANA-493 — the SINGLE source of truth for "is scope locked", replacing two
 * inverse definitions that agreed only by coincidence: a server-side ALLOWLIST
 * (`isScopedLifecycleStatus`) and a client-side COMPLEMENT-OF-OPEN
 * (`isScopeLocked`). They were equivalent over today's 9-value enum, but for any
 * status added to it they returned OPPOSITE answers — the server "not locked"
 * (fail open → still scoping), the client "locked" (hides the scoping UI).
 * Keeping the allowlist form makes the safer, fail-open answer the shared one.
 */
export const SCOPE_LOCKED_PROJECT_STATUSES = [
  'seeding',
  'planning',
  'ready',
  'active',
  'decision_needed',
  'completed',
  'archived',
] as const;

const SCOPE_LOCKED_PROJECT_STATUS_SET: ReadonlySet<string> = new Set(SCOPE_LOCKED_PROJECT_STATUSES);

/**
 * True when `status` is in the scope-locked cohort ({@link SCOPE_LOCKED_PROJECT_STATUSES}).
 *
 * Case-insensitive (server callers pass raw `projects.status` values). A blank,
 * absent, non-string or unrecognised status is NOT locked — callers treat that as
 * "still scoping", which is the safe direction: the worst case is offering the
 * scoping flow on a project that didn't need it, versus hiding it from one that did.
 */
export function isScopeLockedProjectStatus(status: unknown): boolean {
  return (
    typeof status === 'string' &&
    status.length > 0 &&
    SCOPE_LOCKED_PROJECT_STATUS_SET.has(status.toLowerCase())
  );
}

/**
 * Project draft data - live state during briefing session
 * n8n owns this in Redis; Cloud receives snapshots via SSE
 */
export interface ProjectDraft {
  // Core fields
  name?: string;
  project_type?: ProjectType;
  outcome_type?: string;
  decision_intent?: string;
  
  // Ontology elements (structure aligned with Supabase tables)
  bets?: Array<{
    type: string;
    statement: string;
    rationale?: string;
  }>;
  assumptions?: Array<{
    type: string;
    statement: string;
    risk_level: 'low' | 'medium' | 'high';
  }>;
  goals?: Array<{
    type: string;
    prompt: string;
  }>;
  activities?: Array<{
    activity_type: 'meeting' | 'research' | 'autonomous' | 'upload';
    title: string;
    description?: string;
  }>;
  phases?: Array<{
    label: string;
    order_index: number;
  }>;
  status?: ProjectStatus;
  
  // Meta
  confidence_score?: number;
  conversation_id?: string;
  turn_count?: number;
}

export interface ProjectConversationTurn {
  user_message: string;
  ai_response: string;
  timestamp: string;
  turn_number: number;
}

export interface ProjectSessionData {
  session_id: string;
  conversation_id: string;
  started_at: string;
  turn_count: number;
  active: boolean;
}

/**
 * Project event types for SSE
 */
export interface ProjectPlanUpdatedEvent {
  type: "project.plan.updated";
  project_id: string;
  payload: ProjectDraft;
  ts: string;
}

export interface ProjectConversationTurnEvent {
  type: "project.conversation.turn";
  project_id: string;
  payload: ProjectConversationTurn;
  ts: string;
}

export interface ProjectSessionStartedEvent {
  type: "project.session.started";
  project_id: string;
  payload: ProjectSessionData;
  ts: string;
}

export interface ProjectSessionEndedEvent {
  type: "project.session.ended";
  project_id: string;
  payload: { session_id: string; ended_at: string };
  ts: string;
}

export type ProjectEvent = 
  | ProjectPlanUpdatedEvent
  | ProjectConversationTurnEvent
  | ProjectSessionStartedEvent
  | ProjectSessionEndedEvent;

export type ProjectEventType =
  | "project.plan.updated"
  | "project.conversation.turn"
  | "project.session.started"
  | "project.session.ended";

/**
 * n8n response contract for project.turn
 */
export interface ProjectTurnResponse {
  reply: string;
  project_draft?: ProjectDraft;
  missing_fields?: string[];
}

/**
 * n8n response contract for project.start
 */
export interface ProjectStartResponse {
  project_id: string;
}

/**
 * n8n response contract for plan.request
 */
export interface ProjectPlanRequestResponse {
  success: boolean;
  message?: string;
  missing_fields?: string[];
}

// ============================================================================
// SSE MESSAGE TYPES
// ============================================================================

/**
 * SSE message types sent from Gateway to Copilot
 */

export interface TranscriptAppendMessage {
  type: "transcript.append";
  items: TranscriptEntry[];
}

export interface QuestionsReplaceMessage {
  type: "questions.replace";
  items: QuestionSnapshot[];
  reason: string; // e.g., "initial", "question.updated"
}

export interface GoalsReplaceMessage {
  type: "goals.replace";
  items: GoalSnapshot[];
  reason: string; // e.g., "initial", "goal.updated"
}

export interface FeedMessage {
  type: "feed";
  event: RedisEnvelope; // Raw envelope
}

export interface HeartbeatMessage {
  type: "heartbeat";
  ts: number; // Unix timestamp
}

export interface ErrorMessage {
  type: "error";
  code: string; // e.g., "REDIS_UNAVAILABLE", "MEETING_NOT_FOUND"
  message: string; // Human-readable error message
  retryAfter?: number; // Milliseconds until retry (optional)
}

/**
 * Union type for all SSE messages
 */
export type SSEMessage =
  | TranscriptAppendMessage
  | QuestionsReplaceMessage
  | GoalsReplaceMessage
  | FeedMessage
  | HeartbeatMessage
  | ErrorMessage;

// ============================================================================
// ANALYSIS FEED (Agents activity)
// ============================================================================

/**
 * Agent event types for the admin-only Agents feed
 */
export type AgentEventType =
  | "triage.checkpoint"
  | "triage.note"
  | "started.analyst"
  | "analyst.checkpoint"
  | "analyst.note"
  | "started.interviewer"
  | "interviewer.checkpoint";

/**
 * Known agent actors (extendable)
 */
export type AgentActor =
  | "n8n-mission-control"
  | "agent-conductor"
  | "agent-analyst"
  | "agent-interviewer"
  | (string & {}); // Allow custom actors for forward compatibility

/**
 * Analysis entry for Agents tab (admin-only)
 * 
 * Used for displaying agent activity messages in the admin interface.
 * All timestamps are Unix epoch milliseconds for consistency with n8n/Redis.
 */
export interface AnalysisEntry {
  id: string; // Unique identifier per event
  event: AgentEventType | string; // Event type (strictly typed for agent events, string for backward compat)
  actor: AgentActor | string; // Source agent
  message: string; // Human-readable message
  ts: number; // Unix timestamp in milliseconds (required for proper sorting/display)
  
  // Optional linking/context
  qid?: string; // Related question id
  gid?: string; // Related goal id
  window?: string; // Sequence/window reference
  hint?: string; // Short hint for UI tooltips
  data?: Record<string, unknown>; // Event-specific structured payload
  
  // Legacy fields (for backward compatibility with normalized webhook events)
  type?: string;
  action?: string;
  seq?: string | number;
}

export interface AnalysisAppendMessage {
  type: "analysis.append";
  item: AnalysisEntry;
}

// ============================================================================
// CONNECTION STATE
// ============================================================================

export type ConnectionState = "connected" | "reconnecting" | "error" | "disconnected";

// ============================================================================
// GATEWAY CONFIGURATION
// ============================================================================

export interface GatewayConfig {
  REDIS_URL: string;
  SSE_HEARTBEAT_INTERVAL: number;
  REALTIME_GATEWAY_ENABLED: boolean;
  // Feature flag for new conversation gateway
  FEATURE_NEW_CONVERSATION_GATEWAY?: boolean;
  // Agent logging configuration
  AGENT_LOG_ENABLED?: boolean;
  AGENT_LOG_RETENTION?: number;
  // v2 conversation reconnect window in minutes (default 15, range 1-120).
  // Doubles as the TTL for runtime:state / runtime:snapshot Redis keys.
  V2_CONVERSATION_RECONNECT_WINDOW_MIN: number;
}

// ============================================================================
// HELPER TYPES
// ============================================================================

/**
 * Cookie payload (signed)
 */
export interface SessionCookie {
  mid: number;
}

/**
 * Meeting metadata from DB
 */
export interface MeetingMetadata {
  id: number;
  title: string;
  description?: string;
  status: string;
  start_at?: Date;
  end_at?: Date;
}
